/**
 * Thin wrappers over the generated client: the two query keys named once, and
 * the shared mutation callbacks.
 *
 * The mutation-options builder is local rather than the Licenses one, which
 * reads `error.payload` while the generated hooks throw `ApiError` naming that
 * `body` — passing one to the other silently discards the response body
 * `previousSalesforceAccountId` lives in. Convergence is APP-2478.
 *
 * `reason` is audited operator narrative: request body only, never a query
 * key, URL, log line or toast. Toast copy comes from the outcome's i18n key.
 */
import {
  SALESFORCE_INLINE_STATUSES,
  type SalesforceErrorEnvelope,
  decideOutcome,
  isUnrecognisedOutcome,
  parseLinkResult,
  toastMessageKey,
} from '@/features/SalesforceAccounts/lib/salesforceOutcome'
import type {
  GetSalesforceAccountError,
  ListOrganizationsBySalesforceAccountError,
} from '@/generated/http-clients/v3ApiComponents'
import {
  useClearSalesforceAccount,
  useGetSalesforceAccount,
  useListOrganizationsBySalesforceAccount,
  useSetSalesforceAccount,
} from '@/generated/http-clients/v3ApiComponents'
import type {
  SalesforceAccountLink,
  SalesforceAccountLinkResult,
  SalesforceAccountOrgs,
} from '@/generated/http-clients/v3ApiSchemas'
import {
  BASE_TOAST_OPTION,
  ERROR_TOAST_OPTION,
  SUCCESS_TOAST_OPTION,
  showOrUpdateToast,
} from '@/hooks/useToast/toastUtils'
import type { QueryClient, UseQueryOptions } from '@tanstack/react-query'

// --- Query keys ---
//
// Both mirror `queryKeyFn` in `v3ApiContext.ts`: the operation path split on
// `/`, empty segments dropped, `{param}` segments replaced by the resolved
// path param. Neither query takes query params or a body, so nothing is
// appended.

/** Key of `useGetSalesforceAccount` for one organisation. */
export function orgSalesforceLinkKey(orgId: string): unknown[] {
  return ['api', 'v3', 'orgs', orgId, 'salesforce-account']
}

/** Key of `useListOrganizationsBySalesforceAccount` for one account. */
export function salesforceAccountOrgsKey(
  salesforceAccountId: string,
): unknown[] {
  return ['api', 'v3', 'salesforce-accounts', salesforceAccountId, 'orgs']
}

/**
 * Invalidates the forward link and every affected reverse lookup.
 *
 * `accountIds` is a list, not a single id, because a re-point changes the
 * answer for *two* accounts: the one that lost the organisation and the one
 * that gained it. Invalidating only the new one leaves a stale "still linked"
 * row on the old account's page.
 *
 * Blank ids are dropped (an unlinked org has none) and duplicates collapse, so
 * clearing or re-submitting the same value does not fire the same
 * invalidation twice.
 */
export function invalidateSalesforceLink(
  queryClient: QueryClient,
  orgId: string,
  accountIds: string[],
) {
  queryClient.invalidateQueries({ queryKey: orgSalesforceLinkKey(orgId) })
  for (const accountId of new Set(accountIds.filter(Boolean))) {
    queryClient.invalidateQueries({
      queryKey: salesforceAccountOrgsKey(accountId),
    })
  }
}

// --- Hooks ---

/**
 * Reads the organisation's current link. A `503` means the account service
 * could not be asked, which is materially different from `linked: false` and
 * must never be rendered as "unlinked".
 */
export function useOrgSalesforceLink<TData = SalesforceAccountLink>(
  orgId: string,
  options?: Omit<
    UseQueryOptions<SalesforceAccountLink, GetSalesforceAccountError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) {
  return useGetSalesforceAccount<TData>({ pathParams: { orgId } }, options)
}

export function useSetOrgSalesforceLink(
  options?: Parameters<typeof useSetSalesforceAccount>[0],
) {
  return useSetSalesforceAccount(options)
}

/** Same compare-and-swap rule as the set. */
export function useClearOrgSalesforceLink(
  options?: Parameters<typeof useClearSalesforceAccount>[0],
) {
  return useClearSalesforceAccount(options)
}

/**
 * The reverse lookup. An empty `orgs` array is a normal `200`, never an error
 * and never a `404`; a failed query is the opposite answer and the two must
 * stay visually distinct.
 */
export function useOrgsBySalesforceAccount<TData = SalesforceAccountOrgs>(
  salesforceAccountId: string,
  options?: Omit<
    UseQueryOptions<
      SalesforceAccountOrgs,
      ListOrganizationsBySalesforceAccountError,
      TData
    >,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) {
  return useListOrganizationsBySalesforceAccount<TData>(
    { pathParams: { salesforceAccountId } },
    options,
  )
}

// --- Shared mutation callbacks ---

/** Toast copy for one outcome, already resolved from i18n by the caller. */
export type SalesforceToastCopy = {
  title: string
  description?: string
}

/**
 * Resolved lazily rather than at module scope: `toastUtils` is a common target
 * for partial `vi.mock`, and spreading `BASE_TOAST_OPTION` at module scope
 * makes this file throw on import for any spec whose mock omits that export,
 * including specs that merely have this module somewhere in their import
 * graph. Evaluating inside the call keeps the blast radius to code that
 * actually raises a toast.
 *
 * `toastUtils` has no neutral/info sibling to the three exported options, hence
 * the local composition.
 */
function toastOptionFor(kind: 'success' | 'neutral' | 'error') {
  if (kind === 'success') {
    return SUCCESS_TOAST_OPTION
  }
  if (kind === 'error') {
    return ERROR_TOAST_OPTION
  }
  return { ...BASE_TOAST_OPTION, type: 'info' } as const
}

export type BuildSalesforceMutationOptionsArgs = {
  queryClient: QueryClient
  orgId: string
  /** Every account whose reverse lookup this write can change. */
  accountIds: string[]
  toastId: string
  /**
   * Resolves an outcome `messageKey` to toast copy. Injected so the toast can
   * only ever say what the key says, never what the operator typed.
   */
  resolveCopy: (messageKey: string) => SalesforceToastCopy
  onClose: () => void
  /**
   * Receives the failure for the statuses the dialog renders in place: both
   * 409s (the conflict panel), 400 (field error), 503 (submit disabled) and
   * 502 (in-dialog error).
   *
   * The dialog must copy what it needs into its own `useState` here: reading
   * it back off `mutation.error` breaks on retry, because `mutate()` clears
   * the error and blanks the panel mid-retry.
   */
  onInlineError?: (envelope: SalesforceErrorEnvelope) => void
  /**
   * Called for a `2xx` whose `outcome` this screen does not recognise. That
   * answer is indeterminate — the write may or may not have landed — so the
   * dialog must say so and re-read before it lets the operator submit again.
   */
  onUnrecognised?: () => void
}

/**
 * Builds the `onSuccess` / `onError` pair shared by the link, re-point and
 * clear mutations.
 *
 * `onSuccess` does not assume success: a `200` still carries an outcome, and
 * `unchanged` means nothing was written, so it gets a neutral toast rather
 * than a success one.
 *
 * `onError` suppresses the toast for `SALESFORCE_INLINE_STATUSES`, handing the
 * envelope to `onInlineError` instead. Anything else toasts and closes: it is
 * not a state the dialog can render or the operator can act on.
 */
export function buildSalesforceMutationOptions(
  args: BuildSalesforceMutationOptionsArgs,
) {
  const {
    queryClient,
    orgId,
    accountIds,
    toastId,
    resolveCopy,
    onClose,
    onInlineError,
    onUnrecognised,
  } = args

  return {
    onSuccess: (data: SalesforceAccountLinkResult) => {
      const decision = decideOutcome(data.outcome)

      if (decision.invalidates) {
        invalidateSalesforceLink(queryClient, orgId, [
          ...accountIds,
          ...(data.salesforceAccountId ? [data.salesforceAccountId] : []),
          ...(data.previousSalesforceAccountId
            ? [data.previousSalesforceAccountId]
            : []),
        ])
      }

      if (decision.toast) {
        showOrUpdateToast(toastId, {
          ...toastOptionFor(decision.toast),
          ...resolveCopy(decision.messageKey),
        })
      }

      if (decision.closesDialog) {
        onClose()
      }

      // Last, and only after the invalidation above has been queued: the
      // dialog stays open and indeterminate until that read settles.
      if (isUnrecognisedOutcome(decision)) {
        onUnrecognised?.()
      }
    },

    onError: (error: unknown) => {
      const envelope = parseLinkResult(error)

      if (SALESFORCE_INLINE_STATUSES.includes(envelope.status)) {
        onInlineError?.(envelope)
        return
      }

      // The copy comes from the outcome key alone. The response `detail` is
      // deliberately not surfaced: the contract says it never carries the
      // operator's `reason`, but a toast is the wrong place to trust that.
      const decision = decideOutcome(envelope.result?.outcome ?? '')
      showOrUpdateToast(toastId, {
        ...ERROR_TOAST_OPTION,
        ...resolveCopy(toastMessageKey(decision)),
      })
      onClose()
    },
  }
}

// Re-exported so the forms name their request shapes through the feature's own
// api module rather than reaching into `src/generated/` directly.
export type {
  ClearSalesforceAccountVariables,
  SetSalesforceAccountVariables,
} from '@/generated/http-clients/v3ApiComponents'
